import type { InsightFilter } from '@api/api/insights';
import type { ThemeSchemaResponse } from '@api/api/theme-overview';
import { useQuery } from '@tanstack/react-query';

import { useMemo } from 'react';
import {
  fetchSubjectMentions,
  QUERY_KEY_THEME_MENTIONS,
} from '../services/theme-mentions-service';
import { buildSentimentBreakdown } from '../utils/buildSentimentBreakdown';
import { useSentimentScope } from './useSentimentScope';
import { useThemeMentions } from './useThemeMentions';

export const usePulseSentimentBreakdown = (theme: ThemeSchemaResponse) => {
  const scope = useSentimentScope(theme);
  const mentions = useThemeMentions({
    ...scope,
    enabled: scope.isReady,
  });

  return {
    data: useMemo(() => {
      if (!mentions.data || !scope.isReady) {
        return undefined;
      }

      return buildSentimentBreakdown(mentions.data.mentions);
    }, [mentions.data, scope.isReady]),
    isLoading: mentions.isLoading || scope.isLoading,
    isError: mentions.isError || scope.isError,
  };
};

export const useEntityPulseSentimentBreakdown = ({
  id,
  subjectType,
  scenarioInsights,
}: {
  id: string;
  subjectType: 'entity';
  scenarioInsights?: InsightFilter[];
}) => {
  const mentions = useQuery({
    queryKey: [QUERY_KEY_THEME_MENTIONS, id, subjectType],
    queryFn: ({ signal }) => fetchSubjectMentions(id, subjectType, signal),
  });

  return {
    data: useMemo(
      () =>
        mentions.data || scenarioInsights
          ? buildSentimentBreakdown(
              mentions.data?.mentions ?? [],
              scenarioInsights
            )
          : undefined,
      [mentions.data, scenarioInsights]
    ),
    isLoading: mentions.isLoading,
    isError: mentions.isError,
  };
};
